/**
 * Consome a resposta da validação de endereços em lote quando ela volta assíncrona.
 *
 * A rota de validação em lote responde 200 com os `results[]` enquanto o lote é pequeno; acima
 * do limite ela devolve 202 com um `id_processo` e manda a validação para o worker Python, para
 * a conexão não ficar presa até o timeout do browser. Este mixin esconde essa diferença da
 * tela: `consumirRespostaValidacaoBulk` devolve sempre o corpo no formato síncrono, tendo ou
 * não passado pelo scheduler.
 *
 * O mixin não chama a API direto: quem faz as chamadas é o componente, em `.vue`, porque o
 * gerador do permissions.manifest.json só varre arquivos `.vue` — um `apiController.endpoints`
 * escondido aqui faria a permissão do endpoint sumir da tela. O componente precisa então
 * fornecer dois métodos:
 *
 *   - `buscarProcessoScheduler(idProcesso)`  -> scheduler/getprocessoschedulerbyid
 *   - `aplicarValidacaoScheduler(idProcesso)` -> aplicavalidacaobulk
 */

/** Intervalo entre consultas ao scheduler, igual ao usado na Roteirização. */
const POLL_MS = 5000;

/** Teto de espera do processo. Lote grande de validação leva minutos, não segundos. */
const WAIT_MS = 30 * 60 * 1000;

export default {
  data() {
    return {
      /** Verdadeiro enquanto um processo de validação está sendo aguardado no scheduler. */
      validacaoSchedulerAtiva: false,
      /** Progresso parcial publicado pelo worker (`results/<id>.progress.json`). */
      validacaoSchedulerProgresso: { done: 0, total: 0 },
      /** Timer do polling; nulo quando não há processo em andamento. */
      validacaoSchedulerTimer: null,
      /** Evita consultas sobrepostas quando uma resposta demora mais que o intervalo. */
      validacaoSchedulerEmVoo: false,
    };
  },
  computed: {
    /**
     * Texto de carregamento da tabela: sem scheduler é o "Carregando..." de sempre; com
     * scheduler avisa que a validação está rodando, com o contador quando o worker já publicou.
     */
    validacaoLoadingText() {
      if (!this.validacaoSchedulerAtiva) return this.$t('system.messages.loading');
      const progresso = this.validacaoSchedulerProgresso;
      if (progresso && progresso.total > 0) {
        return this.$t('addressValidation.batchValidatingProgress', {
          done: progresso.done,
          total: progresso.total,
        });
      }
      return this.$t('addressValidation.batchValidating');
    },
  },
  beforeDestroy() {
    this.encerrarValidacaoScheduler();
  },
  methods: {
    /**
     * Devolve o corpo da validação em lote no formato síncrono (`{ results: [...] }`).
     *
     * Resposta 200 volta na hora. Resposta 202 aguarda o processo no scheduler e então pede à
     * API que grave o resultado do worker, que responde com os mesmos `results[]`.
     *
     * @param {object} response resposta do POST de validação em lote.
     * @param {number} totalItens quantidade enviada, usada como total enquanto o worker não
     *   publica o progresso.
     */
    async consumirRespostaValidacaoBulk(response, totalItens) {
      const data = (response && response.data) || {};
      const assincrono = (response && response.status === 202) || data.mode === 'async';
      if (!assincrono || !data.id_processo) return data;

      const total = Number(data.total);
      this.validacaoSchedulerProgresso = {
        done: 0,
        total: Number.isFinite(total) && total > 0 ? total : Number(totalItens) || 0,
      };
      this.validacaoSchedulerAtiva = true;

      await this.aguardarProcessoScheduler(data.id_processo);
      const aplicada = await this.aplicarValidacaoScheduler(data.id_processo);
      return (aplicada && aplicada.data) || {};
    },
    /**
     * Resolve quando o processo chega a DONE; rejeita em FAILED, CANCELED ou estouro do teto
     * de espera. O erro sobe para o `catch` de quem chamou, que já sabe alertar o usuário.
     */
    aguardarProcessoScheduler(idProcesso) {
      this.pararPollingValidacaoScheduler();
      const limite = Date.now() + WAIT_MS;
      return new Promise((resolve, reject) => {
        const encerra = (fn, arg) => {
          this.pararPollingValidacaoScheduler();
          fn(arg);
        };
        const consulta = async () => {
          if (this.validacaoSchedulerEmVoo) return;
          if (Date.now() > limite) {
            encerra(reject, new Error(this.$t('candidate.validationRequestTimeout')));
            return;
          }
          this.validacaoSchedulerEmVoo = true;
          try {
            const resposta = await this.buscarProcessoScheduler(idProcesso);
            const corpo = (resposta && resposta.data) || {};
            if (corpo.erro) {
              encerra(reject, new Error(corpo.mensagem || this.$t('addressValidation.batchValidationFailed')));
              return;
            }
            const processo = corpo.processo || {};
            if (processo.status === 'PENDING' || processo.status === 'EXECUTING') {
              this.aplicarProgressoValidacaoScheduler(corpo.progresso);
              return;
            }
            if (processo.status === 'DONE') {
              encerra(resolve);
              return;
            }
            encerra(reject, new Error(this.mensagemFalhaValidacaoScheduler(processo)));
          } catch (e) {
            const msg = (e.response && e.response.data
              && (e.response.data.mensagem || e.response.data.message))
              || (e && e.message)
              || this.$t('addressValidation.batchValidationFailed');
            encerra(reject, e && e.message === msg ? e : new Error(msg));
          } finally {
            this.validacaoSchedulerEmVoo = false;
          }
        };
        this.validacaoSchedulerTimer = setInterval(consulta, POLL_MS);
        consulta();
      });
    },
    /**
     * Texto de falha do processo para o usuário. O scheduler grava em `mensagem_scheduler` uma
     * chave técnica (ex.: MSG_BLOQUEIO_ALLOWLIST) que não diz nada a quem está validando
     * endereços, então aqui ela vira uma mensagem da própria validação.
     */
    mensagemFalhaValidacaoScheduler(processo) {
      const chave = processo && processo.mensagem_scheduler;
      if (chave === 'MSG_BLOQUEIO_ALLOWLIST') {
        return this.$t('addressValidation.batchValidationBlocked');
      }
      if (chave === 'MSG_INTERRUPCAO_SIGTERM' || chave === 'MSG_INTERRUPCAO_SIGTERM_FALHOU') {
        return this.$t('addressValidation.batchValidationInterrupted');
      }
      return this.$t('addressValidation.batchValidationFailed');
    },
    /**
     * Aplica o progresso parcial do worker. Payload sem total é ignorado: durante PENDING e
     * durante a geocodificação na API o total ainda não é conhecido, e a mensagem sem contador
     * é a correta.
     */
    aplicarProgressoValidacaoScheduler(progresso) {
      const total = Number(progresso && progresso.total);
      if (!Number.isFinite(total) || total <= 0) return;
      const done = Number(progresso.done);
      this.validacaoSchedulerProgresso = {
        done: Number.isFinite(done) ? done : 0,
        total,
      };
    },
    /** Para o polling sem mexer no estado exibido (a promessa em curso decide o desfecho). */
    pararPollingValidacaoScheduler() {
      if (this.validacaoSchedulerTimer != null) {
        clearInterval(this.validacaoSchedulerTimer);
        this.validacaoSchedulerTimer = null;
      }
      this.validacaoSchedulerEmVoo = false;
    },
    /** Encerra a espera e devolve a tela ao estado normal de carregamento. */
    encerrarValidacaoScheduler() {
      this.pararPollingValidacaoScheduler();
      this.validacaoSchedulerAtiva = false;
      this.validacaoSchedulerProgresso = { done: 0, total: 0 };
    },
  },
};
